import { useState, MouseEvent } from "react";
import { useNavigate, useLocation } from "react-router";
import { AUTH_TOKEN_USER, FEEDBACK_FORM_URL } from "@/constants";
import { ListItemIcon, Menu } from "@/components/ui/Menu";
import { MenuItem } from "@/components/ui/MenuItem";
import {
  Logout,
  AccountCircle,
  AssessmentOutlined,
  GroupOutlined,
  FeedbackOutlined,
} from "@mui/icons-material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  BackButtonStyled,
  HeaderStyled,
  HeaderLeftStyled,
  BrandStyled,
  AccountTriggerStyled,
  AccountAvatarStyled,
  MenuHeaderStyled,
  MenuHeaderTextStyled,
  MenuHeaderNameStyled,
  MenuHeaderEmailStyled,
  MenuDividerStyled,
} from "./header-style";

export const Header = ({
  onLogout,
  userData,
}: {
  onLogout: () => void;
  userData?: any;
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const openMenu = Boolean(anchorEl);

  const handleMenuClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // Prefer the Google name, fall back to the email's local part.
  const email = userData?.email || localStorage.getItem(AUTH_TOKEN_USER) || "";
  const emailPrefix = email ? email.split("@")[0] : null;
  const userName = userData?.name || emailPrefix || "Account";
  // Only a real name earns its own line. Otherwise `userName` is just the
  // email's local part, and showing it above the full address prints the same
  // string twice.
  const hasRealName = Boolean(userData?.name);
  const userPicture = userData?.picture;
  const initial = userName.charAt(0).toUpperCase();
  const isHome = location.pathname === "/";

  return (
    <HeaderStyled>
      <HeaderLeftStyled>
        {isHome ? (
          <BrandStyled type="button" onClick={() => navigate("/")}>
            Monthly
          </BrandStyled>
        ) : (
          <BackButtonStyled
            type="button"
            onClick={() => navigate("/")}
            data-testid="back-button"
          >
            <ChevronLeftIcon />
            back
          </BackButtonStyled>
        )}
      </HeaderLeftStyled>

      <AccountTriggerStyled
        onClick={handleMenuClick}
        type="button"
        aria-haspopup="true"
        aria-expanded={openMenu}
        data-testid="account-menu-trigger"
      >
        <AccountAvatarStyled src={userPicture || undefined} alt={userName}>
          {!userPicture && initial}
        </AccountAvatarStyled>
        <ExpandMoreIcon fontSize="small" />
      </AccountTriggerStyled>

      <Menu
        anchorEl={anchorEl}
        id="account-menu"
        open={openMenu}
        onClose={handleMenuClose}
        onClick={handleMenuClose}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <MenuHeaderStyled>
          <AccountAvatarStyled src={userPicture || undefined} alt={userName}>
            {!userPicture && initial}
          </AccountAvatarStyled>
          <MenuHeaderTextStyled>
            {hasRealName && (
              <MenuHeaderNameStyled>{userName}</MenuHeaderNameStyled>
            )}
            {email ? (
              <MenuHeaderEmailStyled>{email}</MenuHeaderEmailStyled>
            ) : (
              <MenuHeaderNameStyled>{userName}</MenuHeaderNameStyled>
            )}
          </MenuHeaderTextStyled>
        </MenuHeaderStyled>

        <MenuDividerStyled />

        <MenuItem onClick={() => navigate("/groups")}>
          <ListItemIcon>
            <GroupOutlined fontSize="small" />
          </ListItemIcon>
          Groups
        </MenuItem>
        <MenuItem onClick={() => navigate("/settings")}>
          <ListItemIcon>
            <AccountCircle fontSize="small" />
          </ListItemIcon>
          Settings
        </MenuItem>
        <MenuItem onClick={() => navigate("/reports")}>
          <ListItemIcon>
            <AssessmentOutlined fontSize="small" />
          </ListItemIcon>
          Reports & Data
        </MenuItem>
        {/* An anchor rather than a navigate(), so it opens in a new tab and
            still honours middle-click and copy-link-address. */}

        <MenuDividerStyled />

        {/* Separated from the destinations above: this one leaves the app, and
            the trailing icon is the convention that says so. */}
        <MenuItem
          component="a"
          href={FEEDBACK_FORM_URL}
          target="_blank"
          rel="noreferrer"
          data-testid="feedback-link"
        >
          <ListItemIcon>
            <FeedbackOutlined fontSize="small" />
          </ListItemIcon>
          <span style={{ flex: 1 }}>Send feedback</span>
          <OpenInNewIcon fontSize="small" sx={{ ml: 1, opacity: 0.6 }} />
        </MenuItem>

        <MenuDividerStyled />

        <MenuItem onClick={onLogout}>
          <ListItemIcon>
            <Logout fontSize="small" />
          </ListItemIcon>
          Log out
        </MenuItem>
      </Menu>
    </HeaderStyled>
  );
};
